/**
 * Options schema (09 §4) + persistence. Flat JSON in localStorage under
 * `disclike.options.v1`; unknown keys ignored on load, missing keys → default.
 * Bindings live separately under `disclike.bindings.v1` (05 §6). Changes apply
 * live through `subscribe`. Pure enough to test in Node with a fake storage.
 */
import { DEFAULT_BINDINGS, type Bindings } from './input.js'

export const OPTIONS_KEY = 'disclike.options.v1'
export const BINDINGS_KEY = 'disclike.bindings.v1'

export type OptionTab = 'audio' | 'a11y' | 'hud' | 'input' | 'video' | 'profile' | 'onboarding'

export interface Options {
  'audio.master': number
  'audio.sfx': number
  'audio.music': number
  'audio.ui': number
  'audio.voice': number
  'audio.muteOnBlur': boolean
  'audio.reduced': boolean
  'audio.hubMusic': boolean
  'audio.mono': boolean
  'a11y.reducedMotion': boolean
  'a11y.flashReduction': boolean
  'a11y.highContrast': boolean
  'a11y.patternStrength': number
  'a11y.holdToToggleShield': boolean
  'a11y.uiScaleText': number
  'a11y.hubHitstop': boolean
  'hud.scale': number
  'hud.bouncePreview': boolean
  'hud.showPing': boolean
  'hud.killFeed': boolean
  'hud.offscreenDiscs': boolean
  'hud.teammateMini': boolean
  /** 05 §5 lead marker: ghost ring where a moving enemy will be when a disc thrown now lands. */
  'hud.leadMarker': boolean
  'input.aimAssist': boolean
  /** 05 §3 playtest experiment (2026-09-13): the gamepad's 6°/4 m snap on the mouse too. Default off — the spec says no assist on mouse. */
  'input.mouseAimAssist': boolean
  'input.stickCurve': 'linear' | 'squared'
  'input.leftDeadzone': number
  'input.rightDeadzone': number
  'input.invertY': boolean
  'video.letterbox': 'fit' | 'fill'
  'video.fpsCap': 'vsync' | '60' | '30'
  'video.showFps': boolean
  'profile.displayName': string
  'onboarding.completed': boolean
  'onboarding.prompts': Record<string, boolean>
}

export type OptionKey = keyof Options

export type OptionSpec =
  | { kind: 'number'; min: number; max: number; step: number; label: string; tab: OptionTab }
  | { kind: 'bool'; label: string; tab: OptionTab }
  | { kind: 'enum'; values: readonly string[]; label: string; tab: OptionTab }
  | { kind: 'string'; min: number; max: number; label: string; tab: OptionTab }
  | { kind: 'object'; label: string; tab: OptionTab }

/** Every key, its default, its UI spec. The order is the options screen order. */
export const OPTION_SPECS: { [K in OptionKey]: OptionSpec } = {
  'audio.master': { kind: 'number', min: 0, max: 1, step: 0.05, label: 'MASTER', tab: 'audio' },
  'audio.sfx': { kind: 'number', min: 0, max: 1, step: 0.05, label: 'EFFECTS', tab: 'audio' },
  'audio.music': { kind: 'number', min: 0, max: 1, step: 0.05, label: 'MUSIC', tab: 'audio' },
  'audio.ui': { kind: 'number', min: 0, max: 1, step: 0.05, label: 'INTERFACE', tab: 'audio' },
  'audio.voice': { kind: 'number', min: 0, max: 1, step: 0.05, label: 'CALL-OUTS', tab: 'audio' },
  'audio.muteOnBlur': { kind: 'bool', label: 'MUTE ON WINDOW BLUR', tab: 'audio' },
  'audio.reduced': { kind: 'bool', label: 'REDUCED AUDIO', tab: 'audio' },
  'audio.hubMusic': { kind: 'bool', label: 'HUB MUSIC', tab: 'audio' },
  'audio.mono': { kind: 'bool', label: 'MONO', tab: 'audio' },
  'a11y.reducedMotion': { kind: 'bool', label: 'REDUCED MOTION', tab: 'a11y' },
  'a11y.flashReduction': { kind: 'bool', label: 'FLASH REDUCTION', tab: 'a11y' },
  'a11y.highContrast': { kind: 'bool', label: 'HIGH CONTRAST', tab: 'a11y' },
  'a11y.patternStrength': { kind: 'number', min: 0, max: 2, step: 0.25, label: 'PATTERN STRENGTH', tab: 'a11y' },
  'a11y.holdToToggleShield': { kind: 'bool', label: 'TAP TO TOGGLE SHIELD', tab: 'a11y' },
  'a11y.uiScaleText': { kind: 'number', min: 1, max: 1.5, step: 0.1, label: 'TEXT SCALE', tab: 'a11y' },
  'a11y.hubHitstop': { kind: 'bool', label: 'HUB HITSTOP', tab: 'a11y' },
  'hud.scale': { kind: 'number', min: 0.8, max: 1.4, step: 0.1, label: 'HUD SCALE', tab: 'hud' },
  'hud.bouncePreview': { kind: 'bool', label: 'BOUNCE PREVIEW', tab: 'hud' },
  'hud.showPing': { kind: 'bool', label: 'SHOW PING', tab: 'hud' },
  'hud.killFeed': { kind: 'bool', label: 'KILL FEED', tab: 'hud' },
  'hud.offscreenDiscs': { kind: 'bool', label: 'OFF-SCREEN DISC CHEVRONS', tab: 'hud' },
  'hud.teammateMini': { kind: 'bool', label: 'TEAMMATE MINI', tab: 'hud' },
  'hud.leadMarker': { kind: 'bool', label: 'LEAD MARKER', tab: 'hud' },
  'input.aimAssist': { kind: 'bool', label: 'GAMEPAD AIM ASSIST', tab: 'input' },
  'input.mouseAimAssist': { kind: 'bool', label: 'MOUSE AIM ASSIST (PLAYTEST)', tab: 'input' },
  'input.stickCurve': { kind: 'enum', values: ['linear', 'squared'], label: 'STICK CURVE', tab: 'input' },
  'input.leftDeadzone': { kind: 'number', min: 0.05, max: 0.4, step: 0.01, label: 'LEFT DEADZONE', tab: 'input' },
  'input.rightDeadzone': { kind: 'number', min: 0.05, max: 0.4, step: 0.01, label: 'RIGHT DEADZONE', tab: 'input' },
  'input.invertY': { kind: 'bool', label: 'INVERT Y (RESERVED)', tab: 'input' },
  'video.letterbox': { kind: 'enum', values: ['fit', 'fill'], label: 'LETTERBOX', tab: 'video' },
  'video.fpsCap': { kind: 'enum', values: ['vsync', '60', '30'], label: 'FPS CAP', tab: 'video' },
  'video.showFps': { kind: 'bool', label: 'SHOW FPS', tab: 'video' },
  'profile.displayName': { kind: 'string', min: 3, max: 16, label: 'DISPLAY NAME', tab: 'profile' },
  'onboarding.completed': { kind: 'bool', label: 'TRAINING COMPLETED', tab: 'onboarding' },
  'onboarding.prompts': { kind: 'object', label: 'DISMISSED PROMPTS', tab: 'onboarding' },
}

export function defaultOptions(prefersReducedMotion = false): Options {
  return {
    'audio.master': 0.8, 'audio.sfx': 1.0, 'audio.music': 0.6, 'audio.ui': 0.9, 'audio.voice': 0.8,
    'audio.muteOnBlur': true, 'audio.reduced': false, 'audio.hubMusic': false, 'audio.mono': false,
    'a11y.reducedMotion': prefersReducedMotion, 'a11y.flashReduction': false, 'a11y.highContrast': false,
    'a11y.patternStrength': 1, 'a11y.holdToToggleShield': false, 'a11y.uiScaleText': 1, 'a11y.hubHitstop': true,
    'hud.scale': 1.0, 'hud.bouncePreview': true, 'hud.showPing': false, 'hud.killFeed': true, 'hud.offscreenDiscs': true, 'hud.teammateMini': true, 'hud.leadMarker': true,
    'input.aimAssist': true, 'input.mouseAimAssist': false, 'input.stickCurve': 'squared', 'input.leftDeadzone': 0.18, 'input.rightDeadzone': 0.25, 'input.invertY': false,
    'video.letterbox': 'fit', 'video.fpsCap': 'vsync', 'video.showFps': false,
    'profile.displayName': '',
    'onboarding.completed': false, 'onboarding.prompts': {},
  }
}

export const OPTION_KEYS = Object.keys(OPTION_SPECS) as OptionKey[]

/** Minimal storage shape so tests can pass a Map-backed fake. */
export interface StorageLike { getItem(k: string): string | null; setItem(k: string, v: string): void; removeItem(k: string): void }

export function memoryStorage(): StorageLike {
  const m = new Map<string, string>()
  return { getItem: (k) => m.get(k) ?? null, setItem: (k, v) => { m.set(k, v) }, removeItem: (k) => { m.delete(k) } }
}

/** Validate one value against its spec; returns undefined if unusable. */
export function coerceOption<K extends OptionKey>(key: K, value: unknown): Options[K] | undefined {
  const spec = OPTION_SPECS[key]
  switch (spec.kind) {
    case 'number': { if (typeof value !== 'number' || !Number.isFinite(value)) return undefined; return Math.min(spec.max, Math.max(spec.min, value)) as Options[K] }
    case 'bool': return typeof value === 'boolean' ? (value as Options[K]) : undefined
    case 'enum': return typeof value === 'string' && spec.values.includes(value) ? (value as Options[K]) : undefined
    case 'string': return typeof value === 'string' ? (value.slice(0, spec.max) as Options[K]) : undefined
    case 'object': return value && typeof value === 'object' && !Array.isArray(value) ? (value as Options[K]) : undefined
  }
}

/** Parse a stored JSON blob: unknown keys ignored, bad values → default. */
export function parseOptions(json: string | null, defaults: Options = defaultOptions()): Options {
  const out: Options = { ...defaults, 'onboarding.prompts': { ...defaults['onboarding.prompts'] } }
  if (!json) return out
  let raw: unknown
  try { raw = JSON.parse(json) } catch { return out }
  if (!raw || typeof raw !== 'object') return out
  for (const key of OPTION_KEYS) {
    if (!(key in (raw as Record<string, unknown>))) continue
    const v = coerceOption(key, (raw as Record<string, unknown>)[key])
    if (v !== undefined) (out as unknown as Record<string, unknown>)[key] = v
  }
  return out
}

/** Stored bindings → full table: known actions take the stored codes (≤ 4), missing ones their default.
 *  S10 (05 §2) v1 → v2 shape migration, additive under the same key: a blob without `jump` gains
 *  `jump: ['Space']` and loses `Space` from `throw` (Space used to be the throw secondary). */
export function migrateBindings(raw: Partial<Record<string, unknown>>): Bindings {
  const out: Bindings = { ...DEFAULT_BINDINGS }
  for (const k of Object.keys(out) as (keyof Bindings)[]) out[k] = DEFAULT_BINDINGS[k].slice()
  for (const k of Object.keys(out) as (keyof Bindings)[]) {
    const v = raw[k]
    if (Array.isArray(v) && v.every((c) => typeof c === 'string')) out[k] = (v as string[]).slice(0, 4)
  }
  if (!Array.isArray(raw.jump)) { out.jump = DEFAULT_BINDINGS.jump.slice(); out.throw = out.throw.filter((c) => c !== 'Space') }
  return out
}

export function parseBindings(json: string | null): Bindings {
  if (!json) return migrateBindings({})
  let raw: unknown
  try { raw = JSON.parse(json) } catch { return migrateBindings({}) }
  if (!raw || typeof raw !== 'object') return migrateBindings({})
  return migrateBindings(raw as Partial<Record<string, unknown>>)
}

/** Actions → codes shared by two or more actions (05 §6 conflicts, amber). */
export function bindingConflicts(b: Bindings): Set<string> {
  const seen = new Map<string, number>()
  for (const codes of Object.values(b)) for (const c of codes) seen.set(c, (seen.get(c) ?? 0) + 1)
  return new Set([...seen.entries()].filter(([, n]) => n > 1).map(([c]) => c))
}

export type OptionsListener = (opts: Options, changed: OptionKey | '*') => void

export class OptionsStore {
  values: Options
  bindings: Bindings
  private listeners: OptionsListener[] = []
  private bindingListeners: ((b: Bindings) => void)[] = []

  constructor(private readonly storage: StorageLike, private readonly defaults: Options = defaultOptions()) {
    this.values = parseOptions(storage.getItem(OPTIONS_KEY), defaults)
    this.bindings = parseBindings(storage.getItem(BINDINGS_KEY))
  }

  get<K extends OptionKey>(key: K): Options[K] { return this.values[key] }

  set<K extends OptionKey>(key: K, value: Options[K]): void {
    const v = coerceOption(key, value)
    if (v === undefined || v === this.values[key]) return
    this.values = { ...this.values, [key]: v }
    this.persist()
    for (const l of this.listeners) l(this.values, key)
  }

  /** Record a dismissed first-time prompt (09 §5.2). */
  dismissPrompt(id: string): void {
    if (this.values['onboarding.prompts'][id]) return
    this.values = { ...this.values, 'onboarding.prompts': { ...this.values['onboarding.prompts'], [id]: true } }
    this.persist()
    for (const l of this.listeners) l(this.values, 'onboarding.prompts')
  }

  /** Reset every key on one tab (09 §4). */
  resetTab(tab: OptionTab): void {
    const next = { ...this.values }
    for (const k of OPTION_KEYS) if (OPTION_SPECS[k].tab === tab) (next as unknown as Record<string, unknown>)[k] = this.defaults[k]
    this.values = next
    this.persist()
    for (const l of this.listeners) l(this.values, '*')
  }

  resetAll(): void {
    this.values = { ...this.defaults, 'onboarding.prompts': {} }
    this.persist()
    for (const l of this.listeners) l(this.values, '*')
  }

  setBinding(action: keyof Bindings, codes: string[]): void {
    this.bindings = { ...this.bindings, [action]: codes.slice(0, 4) }
    this.storage.setItem(BINDINGS_KEY, JSON.stringify(this.bindings))
    for (const l of this.bindingListeners) l(this.bindings)
  }

  resetBindings(): void {
    this.bindings = parseBindings(null)
    this.storage.removeItem(BINDINGS_KEY)
    for (const l of this.bindingListeners) l(this.bindings)
  }

  subscribe(fn: OptionsListener): () => void { this.listeners.push(fn); return () => { this.listeners = this.listeners.filter((l) => l !== fn) } }
  subscribeBindings(fn: (b: Bindings) => void): () => void { this.bindingListeners.push(fn); return () => { this.bindingListeners = this.bindingListeners.filter((l) => l !== fn) } }

  private persist(): void { this.storage.setItem(OPTIONS_KEY, JSON.stringify(this.values)) }
}

/** Browser factory: real localStorage + OS reduced-motion preference. */
export function createOptionsStore(): OptionsStore {
  const prm = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches
  const storage: StorageLike = typeof localStorage !== 'undefined' ? localStorage : memoryStorage()
  return new OptionsStore(storage, defaultOptions(prm))
}
